/**
 * BusinessFormsList Component
 * Lista y gestión de formularios asignados a un negocio
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Description as FormIcon,
} from '@mui/icons-material';
import businessFormsService, { BusinessForm } from '../../../services/businessForms';
import AssignFormsDialog from './AssignFormsDialog.tsx';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { formatDate } from '../../../utils/dateUtils';

interface BusinessFormsListProps {
  businessId: number;
  businessName: string;
  businessTimezone?: string;
}

const BusinessFormsList: React.FC<BusinessFormsListProps> = ({ 
  businessId, 
  businessName,
  businessTimezone
}) => {
  const [forms, setForms] = useState<BusinessForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    formId?: number;
    formName?: string;
  }>({ open: false });

  useEffect(() => {
    loadForms();
  }, [businessId]);

  const loadForms = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await businessFormsService.getBusinessForms(businessId);
      setForms(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar formularios');
      console.error('Error cargando formularios:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async () => {
    if (!confirmDelete.formId) return;

    try {
      setError(null);
      await businessFormsService.unassignForm(businessId, confirmDelete.formId);
      setConfirmDelete({ open: false });
      loadForms(); // Recargar lista
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al desasignar formulario');
      setConfirmDelete({ open: false });
    }
  };

  const handleAssignSuccess = () => {
    setOpenAssignDialog(false);
    loadForms(); // Recargar lista
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            📋 Formularios Asignados
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            Gestiona qué formularios están disponibles para <strong>{businessName}</strong>
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setOpenAssignDialog(true)}
        >
          Gestionar Formularios
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Lista de Formularios */}
      {forms.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <FormIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No hay formularios asignados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Este negocio no tiene formularios disponibles para auditorías
              </Typography>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setOpenAssignDialog(true)}
              >
                Asignar Formularios
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <List>
            {forms.map((form, index) => (
              <ListItem
                key={form.id}
                divider={index < forms.length - 1}
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormIcon fontSize="small" color="primary" />
                      <Typography variant="body1" fontWeight={500} component="span">
                        {form.name}
                      </Typography>
                      {form.active && (
                        <Chip label="Activo" size="small" color="success" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'block' }}>
                      <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                        {form.description || 'Sin descripción'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                        Asignado: {formatDate(form.assigned_at, businessTimezone)}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Desasignar formulario">
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => setConfirmDelete({
                        open: true,
                        formId: form.id,
                        formName: form.name,
                      })}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          <CardContent sx={{ backgroundColor: 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              Total: <strong>{forms.length}</strong> formulario{forms.length !== 1 ? 's' : ''} asignado{forms.length !== 1 ? 's' : ''}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Dialog: Asignar Formularios */}
      <AssignFormsDialog
        open={openAssignDialog}
        onClose={() => setOpenAssignDialog(false)}
        businessId={businessId}
        businessName={businessName}
        currentFormIds={forms.map(f => f.id)}
        onSuccess={handleAssignSuccess}
      />

      {/* Dialog: Confirmar Desasignación */}
      <ConfirmDialog
        open={confirmDelete.open}
        title="Desasignar Formulario"
        message={`¿Está seguro que desea desasignar el formulario "${confirmDelete.formName}"? Los auditores ya no podrán usar este formulario para crear auditorías en este negocio.`}
        onConfirm={handleUnassign}
        onCancel={() => setConfirmDelete({ open: false })}
        confirmText="Desasignar"
        confirmColor="error"
      />
    </Box>
  );
};

export default BusinessFormsList;
